import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { fetchProducts, fetchCollections } from "../lib/api";
import { ProductCard } from "../components/ProductCard";

const SORT_OPTIONS = [
  { value: "newest", en: "NEWEST", pt: "MAIS RECENTES" },
  { value: "oldest", en: "OLDEST", pt: "MAIS ANTIGOS" },
  { value: "price_asc", en: "PRICE ↑", pt: "PREÇO ↑" },
  { value: "price_desc", en: "PRICE ↓", pt: "PREÇO ↓" },
  { value: "az", en: "A — Z", pt: "A — Z" },
  { value: "za", en: "Z — A", pt: "Z — A" },
];

export default function Shop() {
  const { lang } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get("collection") || "all";
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    fetchProducts(active === "all" ? undefined : active).then(setProducts);
  }, [active]);
  useEffect(() => {
    fetchCollections().then((d) => setCollections(d.collections));
  }, []);

  const sorted = useMemo(() => {
    const list = [...products];
    const nameField = lang === "pt" ? "name_pt" : "name";
    switch (sort) {
      case "oldest":
        return list.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      case "price_asc":
        return list.sort((a, b) => (a.price || 0) - (b.price || 0));
      case "price_desc":
        return list.sort((a, b) => (b.price || 0) - (a.price || 0));
      case "az":
        return list.sort((a, b) => String(a[nameField] || a.name || "").localeCompare(String(b[nameField] || b.name || "")));
      case "za":
        return list.sort((a, b) => String(b[nameField] || b.name || "").localeCompare(String(a[nameField] || a.name || "")));
      case "newest":
      default:
        return list.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
  }, [products, sort, lang]);

  return (
    <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-12 md:py-20" data-testid="page-shop">
      <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ SHOP</p>
      <h1 className="font-display text-6xl md:text-8xl mt-2 mb-3 leading-none">
        SHOP <span className="text-kult-pink">KULTOPIA.</span>
      </h1>
      <p className="text-kult-muted max-w-xl mb-10">
        {lang === "pt"
          ? "Cápsulas, signature lines, tour merch. Tudo limitado, tudo nosso."
          : "Capsules, signature lines, tour merch. All limited, all ours."}
      </p>

      <div
        className="flex flex-wrap gap-2 md:gap-3 mb-6 border-t border-kult-border pt-5"
        data-testid="shop-filters"
      >
        {collections.map((c) => {
          const isActive = active === c.slug;
          return (
            <button
              key={c.slug}
              onClick={() =>
                setSearchParams(c.slug === "all" ? {} : { collection: c.slug })
              }
              className={`font-display text-lg md:text-xl px-5 py-2 border-2 transition-all ${
                isActive
                  ? "bg-kult-pink text-black border-kult-pink"
                  : "border-kult-border text-white hover:border-kult-pink"
              }`}
              data-testid={`shop-filter-${c.slug}`}
            >
              {lang === "pt" ? c.name_pt : c.name_en}
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between border-b border-kult-border pb-5 mb-10">
        <p className="font-mono text-[11px] tracking-[0.3em] text-kult-muted" data-testid="shop-count">
          {sorted.length} {lang === "pt" ? "PEÇAS" : "PIECES"}
        </p>
        <label className="flex items-center gap-3">
          <span className="font-mono text-[10px] tracking-[0.3em] text-kult-yellow hidden sm:inline">
            {lang === "pt" ? "ORDENAR" : "SORT"}
          </span>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="shop-sort"
            className="bg-kult-black border border-kult-border text-white font-mono text-xs tracking-[0.2em] px-3 py-2 focus:border-kult-pink outline-none cursor-pointer"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value} className="bg-kult-black">
                {lang === "pt" ? opt.pt : opt.en}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
        {sorted.map((p) => (
          <ProductCard key={p.id} product={p} />
        ))}
      </div>

      {sorted.length === 0 && (
        <p className="font-mono text-sm text-kult-muted py-20 text-center">No products yet.</p>
      )}
    </div>
  );
}
